export type Hotel = {
    id: string;
    name: string;
    imageUrl: string;
    priceFrom?: number;
    rating?: number;
    address?: string;
};

export type SearchHotelsParams = {
    city?: string;
    name?: string;
    checkIn?: string; // yyyy-mm-dd
    checkOut?: string; // yyyy-mm-dd
};

const API_URL = process.env.REACT_APP_HOTELS_API_URL;
const API_KEY = process.env.REACT_APP_HOTELS_API_KEY;
const API_HOST = (process.env.REACT_APP_HOTELS_API_HOST || '').trim(); // optional (e.g., RapidAPI host)

function buildHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };

    if (API_KEY) {
        // Try common auth header names
        headers['Authorization'] = `Bearer ${API_KEY}`;
        headers['X-API-Key'] = API_KEY;
        headers['X-RapidAPI-Key'] = API_KEY;
    }
    if (API_HOST) {
        headers['X-RapidAPI-Host'] = API_HOST;
    }
    return headers;
}

function mapApiHotelToHotel(item: any): Hotel {
    return {
        id: String(item.id ?? item.hotel_id ?? item.code ?? Math.random()),
        name: item.name ?? item.hotelName ?? 'Hotel',
        imageUrl: item.imageUrl ?? item.image ?? '/hotell.jpg.png',
        priceFrom: item.priceFrom ?? item.price ?? item.lowestPrice,
        rating: item.rating ?? item.stars ?? item.score,
        address: item.address ?? item.location ?? 'Unknown Location',
    };
}

async function fetchFromApi(params: SearchHotelsParams): Promise<Hotel[]> {
    if (!API_URL) {
        throw new Error('Missing REACT_APP_HOTELS_API_URL');
    }
    const url = new URL(API_URL);
    if (params.city) url.searchParams.set('city', params.city);
    if (params.name) url.searchParams.set('name', params.name);
    if (params.checkIn) url.searchParams.set('checkIn', params.checkIn);
    if (params.checkOut) url.searchParams.set('checkOut', params.checkOut);

    const res = await fetch(url.toString(), {
        method: 'GET',
        headers: buildHeaders(),
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Hotels API error: ${res.status} ${text}`);
    }
    const data = await res.json();

    // Accept common shapes: {hotels: []} or []
    const hotelsArray = Array.isArray(data) ? data : (data.hotels ?? data.results ?? []);
    return hotelsArray.map(mapApiHotelToHotel);
}

function mockTurkishHotels(): Hotel[] {
    return [
        // Istanbul Hotels
        {
            id: 'ist-1',
            name: 'Sultanahmet Palace Hotel',
            imageUrl: '/hote12.jpg.png',
            priceFrom: 85,
            rating: 4.4,
            address: 'Sultanahmet, Istanbul, Turkey',
        },
        {
            id: 'ist-2',
            name: 'Taksim Square Hotel',
            imageUrl: '/hote13.jpg.png',
            priceFrom: 72,
            rating: 4.1,
            address: 'Taksim, Istanbul, Turkey',
        },
        {
            id: 'ist-3',
            name: 'Bosphorus View Residence',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 110,
            rating: 4.6,
            address: 'Besiktas, Istanbul, Turkey',
        },
        {
            id: 'ist-4',
            name: 'Hagia Sophia Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 95,
            rating: 4.7,
            address: 'Sultanahmet, Istanbul, Turkey',
        },
        {
            id: 'ist-5',
            name: 'Galata Tower Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 88,
            rating: 4.3,
            address: 'Galata, Istanbul, Turkey',
        },
        {
            id: 'ist-6',
            name: 'Grand Bazaar Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 78,
            rating: 4.2,
            address: 'Beyazit, Istanbul, Turkey',
        },
        // Cappadocia Hotels
        {
            id: 'cap-1',
            name: 'Cappadocia Cave Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 120,
            rating: 4.3,
            address: 'Goreme, Cappadocia, Turkey',
        },
        {
            id: 'cap-2',
            name: 'Fairy Chimney Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 110,
            rating: 4.2,
            address: 'Uchisar, Cappadocia, Turkey',
        },
        {
            id: 'cap-3',
            name: 'Hot Air Balloon Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 130,
            rating: 4.5,
            address: 'Avanos, Cappadocia, Turkey',
        },
        // Antalya Hotels
        {
            id: 'ant-1',
            name: 'Antalya Beach Resort',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 180,
            rating: 4.6,
            address: 'Lara Beach, Antalya, Turkey',
        },
        {
            id: 'ant-2',
            name: 'Konyaalti Beach Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 170,
            rating: 4.4,
            address: 'Konyaalti, Antalya, Turkey',
        },
        {
            id: 'ant-3',
            name: 'Old Town Antalya Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 160,
            rating: 4.3,
            address: 'Kaleici, Antalya, Turkey',
        },
        // Pamukkale Hotels
        {
            id: 'pam-1',
            name: 'Pamukkale Thermal Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 100,
            rating: 4.2,
            address: 'Pamukkale, Denizli, Turkey',
        },
        {
            id: 'pam-2',
            name: 'Hierapolis Spa Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 120,
            rating: 4.4,
            address: 'Pamukkale, Denizli, Turkey',
        },
        // Bodrum Hotels
        {
            id: 'bod-1',
            name: 'Bodrum Marina Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 200,
            rating: 4.7,
            address: 'Bodrum Marina, Bodrum, Turkey',
        },
        {
            id: 'bod-2',
            name: 'Bodrum Castle Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 190,
            rating: 4.5,
            address: 'Bodrum Castle, Bodrum, Turkey',
        },
        // Ephesus Hotels
        {
            id: 'eph-1',
            name: 'Ephesus Ancient Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 150,
            rating: 4.3,
            address: 'Selcuk, Ephesus, Turkey',
        },
        {
            id: 'eph-2',
            name: 'Virgin Mary House Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 140,
            rating: 4.2,
            address: 'Selcuk, Ephesus, Turkey',
        },
        // Trabzon Hotels
        {
            id: 'tra-1',
            name: 'Sumela Monastery Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 130,
            rating: 4.1,
            address: 'Trabzon, Turkey',
        },
        {
            id: 'tra-2',
            name: 'Uzungol Lake Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 120,
            rating: 4.0,
            address: 'Uzungol, Trabzon, Turkey',
        },
        // Ankara Hotels
        {
            id: 'ank-1',
            name: 'Ankara Capital Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 110,
            rating: 4.2,
            address: 'Kizilay, Ankara, Turkey',
        },
        {
            id: 'ank-2',
            name: 'Ataturk Mausoleum Hotel',
            imageUrl: '/hotell.jpg.png',
            priceFrom: 105,
            rating: 4.1,
            address: 'Anitkabir, Ankara, Turkey',
        },
    ];
}

export async function searchHotels(params: SearchHotelsParams): Promise<Hotel[]> {
    try {
        if (API_URL && API_KEY) {
            return await fetchFromApi(params);
        }
        // No API configured: return mock filtered to Turkish cities
        let results = mockTurkishHotels();
        if (params.city) {
            const lc = params.city.toLowerCase();
            const turkishCities = [
                'istanbul', 'إسطنبول', 'اسطنبول',
                'cappadocia', 'كابادوكيا', 'كبادوكيا',
                'antalya', 'أنطاليا', 'انطاليا',
                'pamukkale', 'باموكالي', 'باموكاله',
                'bodrum', 'بودروم', 'بودروم',
                'ephesus', 'أفسس', 'افسس',
                'trabzon', 'طرابزون', 'طرابزون',
                'ankara', 'أنقرة', 'انقره'
            ];
            const isTurkishCity = turkishCities.some((v) => lc.includes(v.toLowerCase()));
            if (!isTurkishCity) {
                // If user searched a non-Turkish city, return empty set
                results = [];
            } else {
                // Filter by specific city
                results = results.filter(hotel => 
                    hotel.address?.toLowerCase().includes(lc)
                );
            }
        }
        if (params.name) {
            const ln = params.name.toLowerCase();
            results = results.filter((h) => h.name.toLowerCase().includes(ln));
        }
        return results;
    } catch (err) {
        // On failure, fall back to mock to keep UX working
        console.warn('Hotels API failed, using mock data', err);
        return mockTurkishHotels();
    }
}
